import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { settingsAPI } from '../services/api';

function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [settings, setSettings] = useState({
    store_name: 'pos.ekkapan.com',
    store_logo: null
  });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await settingsAPI.get();
        setSettings(response.data);
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    loadSettings();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Helper function to check if user has permission
  const hasPermission = (permissionKey) => {
    if (!user.permissions || !Array.isArray(user.permissions)) {
      return false;
    }
    const permission = user.permissions.find(p => p.permission_key === permissionKey);
    return permission && permission.can_view;
  };

  // Define menu items based on permissions
  const getMenuItems = () => {
    const allMenuItems = [
      { path: '/pos', label: 'หน้าขาย', icon: '🛒', permissionKey: 'pos' },
      { path: '/dashboard', label: 'แดชบอร์ด', icon: '📊', permissionKey: 'dashboard' },
      { path: '/products', label: 'จัดการสินค้า', icon: '📦', permissionKey: 'products' },
      { path: '/categories', label: 'หมวดหมู่สินค้า', icon: '📑', permissionKey: 'categories' },
      { path: '/members', label: 'สมาชิก', icon: '👤', permissionKey: 'members' },
      { path: '/sales', label: 'ประวัติการขาย', icon: '💰', permissionKey: 'sales' },
      { path: '/promotions', label: 'โปรโมชั่น', icon: '🎁', permissionKey: 'promotions' },
      { path: '/stock-count', label: 'นับสต็อก', icon: '📝', permissionKey: 'stock_count' },
      { path: '/stock-history', label: 'สต็อก', icon: '📦', permissionKey: 'stock_history' },
      { path: '/reports', label: 'รายงาน', icon: '📈', permissionKey: 'reports' },
      { path: '/users', label: 'จัดการผู้ใช้', icon: '👥', permissionKey: 'users' },
      { path: '/roles', label: 'จัดการบทบาท', icon: '🔐', permissionKey: 'roles' },
      { path: '/settings', label: 'ตั้งค่าระบบ', icon: '⚙️', permissionKey: 'settings' },
    ];

    return allMenuItems.filter(item => hasPermission(item.permissionKey));
  };

  const menuItems = getMenuItems();

  const openCustomerDisplay = () => {
    window.open('/customer-display', '_blank', 'width=1920,height=1080');
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className="w-64 bg-primary-600 text-white flex flex-col">
        <div className="p-6 flex-shrink-0">
          {settings.store_logo && (
            <div className="flex justify-center mb-3">
              <img
                src={settings.store_logo}
                alt="Logo"
                className="h-16 w-16 object-contain rounded-lg bg-white p-1"
              />
            </div>
          )}
          <h1 className="text-xl font-bold text-center">{settings.store_name || 'pos.ekkapan.com'}</h1>
          <div className="border-t border-primary-500 mt-3 pt-3">
            <p className="text-sm text-primary-100">{user.full_name}</p>
            <p className="text-xs text-primary-200">
              {user.role === 'admin' ? 'ผู้ดูแลระบบ' :
               user.role === 'manager' ? 'ผู้จัดการ' :
               user.role === 'cashier' ? 'พนักงานขาย' :
               user.role}
            </p>
          </div>
        </div>

        <nav className="mt-6 flex-1 overflow-y-auto">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center px-6 py-3 hover:bg-primary-700 transition-colors ${
                location.pathname === item.path ? 'bg-primary-700 border-l-4 border-white' : ''
              }`}
            >
              <span className="mr-3 text-xl">{item.icon}</span>
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="flex-shrink-0 p-6 space-y-2">
          {hasPermission('pos') && (
            <button
              onClick={openCustomerDisplay}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded transition-colors flex items-center justify-center gap-2"
            >
              <span className="text-xl">📺</span>
              <span>หน้าจอลูกค้า</span>
            </button>
          )}
          <button
            onClick={handleLogout}
            className="w-full bg-primary-700 hover:bg-primary-800 text-white py-2 px-4 rounded transition-colors"
          >
            ออกจากระบบ
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>

        {/* Developer Footer - Always at bottom */}
        <footer className="flex-shrink-0 bg-gray-800 text-white py-3 px-6">
          <div className="flex items-center justify-center gap-4 text-sm">
            <span>จัดทำโดย <span className="font-semibold">Ekkapan Jantaen</span></span>
            <div className="flex items-center gap-3">
              <a
                href="https://www.facebook.com/EkkapanJantaen"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 hover:text-blue-400 transition-colors"
                title="Facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span className="hidden sm:inline">Facebook</span>
              </a>
              <a
                href="https://line.me/ti/p/unknown-item"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 hover:text-green-400 transition-colors"
                title="Line ID: unknown-item"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
                </svg>
                <span className="hidden sm:inline">Line</span>
              </a>
              <a
                href="tel:0829748971"
                className="flex items-center gap-1 hover:text-yellow-400 transition-colors"
                title="โทร: 082-974-8971"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="hidden sm:inline">082-974-8971</span>
              </a>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}

export default Layout;
